import React, { useState } from 'react';
import { LifeStageContext } from '../types/lifeStage';
import { Language } from '../types/kundali';
import {
  Calendar,
  Sparkles,
  Shield,
  Clock,
  Compass,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Info,
  Activity,
  Layers
} from 'lucide-react';

interface LifeStageContextBannerProps {
  lifeStageContext: LifeStageContext;
  lang: Language;
  className?: string;
}

export const LifeStageContextBanner: React.FC<LifeStageContextBannerProps> = ({
  lifeStageContext,
  lang,
  className = ''
}) => {
  const isHi = lang === 'hi';
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    years,
    months,
    stage,
    isYoungChild,
    isAdolescent,
    maturationStatuses,
    activeDashaContext,
    gocharContext,
    deshKaalPatraGuidanceEn,
    deshKaalPatraGuidanceHi
  } = lifeStageContext;

  // Filter matured vs pending planets
  const maturedPlanets = maturationStatuses.filter((p) => p.isMatured);

  return (
    <div
      id="life-stage-context-banner"
      className={`rounded-2xl border border-astro-border bg-astro-bg p-3.5 sm:p-4 shadow-2xs transition-all ${className}`}
    >
      {/* Compact Default Single-Row Presentation */}
      <div className="flex flex-wrap items-center justify-between gap-2.5">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Age Badge */}
          <div className="flex items-center gap-1.5 px-3 py-1 rounded-xl bg-astro-card border border-astro-border text-astro-primary font-semibold text-xs sm:text-sm shadow-2xs">
            <Calendar className="w-3.5 h-3.5 text-astro-primary shrink-0" />
            <span>{isHi ? `${years}व ${months}मा` : `Age ${years}y ${months}m`}</span>
          </div>

          {/* Divider */}
          <span className="hidden sm:inline text-astro-border">|</span>

          {/* Life-Stage Tag */}
          <span className="text-xs uppercase font-bold tracking-wider text-astro-text bg-astro-card px-2.5 py-1 rounded-lg border border-astro-border">
            {isHi ? stage.labelHi : stage.labelEn}
          </span>

          {/* Purushartha Pill */}
          <span className="hidden md:inline-flex text-[11px] font-semibold text-astro-secondary-text bg-astro-card px-2.5 py-1 rounded-lg border border-astro-border">
            {stage.primaryPurushartha}
          </span>

          {/* Divider */}
          <span className="hidden md:inline text-astro-border">|</span>

          {/* Active Dasha badge */}
          <div className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-astro-card border border-astro-border text-xs text-astro-text">
            <Clock className="w-3 h-3 text-astro-primary" />
            <span className="font-semibold text-astro-primary">{activeDashaContext.mahadasha}–{activeDashaContext.antardasha}</span>
          </div>

          {/* Gochar Auspicious Transit Badge */}
          {gocharContext.isJupiterGuruBalActive && (
            <span className="hidden lg:inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
              <Sparkles className="w-3 h-3 text-astro-accent" />
              {isHi ? 'गुरु बल सक्रिय' : 'Jupiter Guru Bala Active'}
            </span>
          )}

          {/* Child/Adolescent Protection Indicator */}
          {(isYoungChild || isAdolescent) && (
            <div className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-astro-secondary-surface border border-astro-border text-astro-primary text-xs font-semibold">
              <Shield className="w-3.5 h-3.5 text-astro-accent" />
              <span>{isYoungChild ? (isHi ? 'बाल संरक्षण मार्गदर्शन' : 'Child Guidance Mode') : (isHi ? 'किशोर प्रतिभा मार्गदर्शन' : 'Youth Focus Mode')}</span>
            </div>
          )}
        </div>

        {/* Expand / Collapse Control Button */}
        <button
          id="btn-toggle-life-stage-details"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-astro-card hover:bg-astro-secondary-surface border border-astro-border text-astro-primary rounded-lg text-xs font-bold transition-all min-h-[36px] min-w-[44px] touch-manipulation cursor-pointer"
          aria-expanded={isExpanded}
        >
          <span>{isHi ? (isExpanded ? 'संक्षिप्त' : 'आयु संदर्भ') : (isExpanded ? 'Compact' : 'Stage Context')}</span>
          {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-astro-accent" /> : <ChevronDown className="w-3.5 h-3.5 text-astro-accent" />}
        </button>
      </div>

      {/* Expanded Astrological Maturity & Dynamic Context */}
      {isExpanded && (
        <div className="mt-3.5 pt-3.5 border-t border-astro-border space-y-3.5 animate-fadeIn">
          {/* Desh-Kaal-Patra Baseline Notice */}
          <div className="p-2.5 rounded-xl bg-astro-card border border-astro-border flex items-start gap-2 text-xs text-astro-text">
            <Info className="w-4 h-4 text-astro-primary flex-shrink-0 mt-0.5" />
            <p className="leading-relaxed font-medium">
              {isHi ? deshKaalPatraGuidanceHi : deshKaalPatraGuidanceEn}
            </p>
          </div>

          {/* Grid: Developmental Focus & Dasha-Age Alignment */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
            <div className="p-3 bg-astro-card border border-astro-border rounded-xl space-y-1">
              <div className="flex items-center gap-1.5 font-bold text-astro-text">
                <Compass className="w-3.5 h-3.5 text-astro-primary" />
                <span>{isHi ? 'आयु-आधारित मुख्य विकास लक्ष्य' : 'Stage-Specific Developmental Focus'}</span>
              </div>
              <p className="text-astro-secondary-text leading-relaxed">
                {isHi ? stage.developmentalFocusHi : stage.developmentalFocusEn}
              </p>
              <div className="text-[11px] text-astro-muted pt-1">
                {isHi ? `प्राथमिक भाव क्षेत्र: भाव ${stage.primaryHouseFocus.join(', ')}` : `Primary House Focus: Houses ${stage.primaryHouseFocus.join(', ')}`}
              </div>
            </div>

            <div className="p-3 bg-astro-card border border-astro-border rounded-xl space-y-1">
              <div className="flex items-center gap-1.5 font-bold text-astro-text">
                <Activity className="w-3.5 h-3.5 text-astro-primary" />
                <span>{isHi ? 'दशा एवं गोचर संपात' : 'Dasha & Gochar Age Dynamic'}</span>
              </div>
              <p className="text-astro-secondary-text leading-relaxed">
                {isHi ? activeDashaContext.dashaThemeHi : activeDashaContext.dashaThemeEn}
              </p>
              <p className="text-[11px] text-astro-primary font-medium">
                {isHi ? gocharContext.gocharSummaryHi : gocharContext.gocharSummaryEn}
              </p>
            </div>
          </div>

          {/* Planetary Maturation Status (Naisargika Parashara Ages) */}
          <div className="p-3 bg-astro-card border border-astro-border rounded-xl">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1.5 font-bold text-xs text-astro-text">
                <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                <span>{isHi ? 'ग्रह परिपक्वता आयु (नैसर्गिक पराशरीय वर्ष)' : 'Classical Planetary Maturation Status (Parashari Age)'}</span>
              </div>
              <span className="text-[10px] text-astro-secondary-text font-medium">
                {maturedPlanets.length}/9 {isHi ? 'परिपक्व' : 'Matured'}
              </span>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-9 gap-2">
              {maturationStatuses.map((p) => {
                return (
                  <div
                    key={p.planet}
                    className={`p-2 rounded-lg border text-center text-xs flex flex-col items-center justify-between transition-all ${
                      p.isMatured
                        ? 'bg-emerald-50/90 border-emerald-300 text-emerald-900 font-bold'
                        : 'bg-astro-bg border-astro-border text-astro-secondary-text'
                    }`}
                  >
                    <span className="font-bold text-[11px]">{p.planet}</span>
                    <span className="text-[10px] my-0.5 font-medium">{p.naturalMaturityAge}y</span>
                    <span className="text-[9px] flex items-center gap-0.5">
                      {p.isMatured ? (
                        <>
                          <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" />
                          <span>{isHi ? 'सक्रिय' : 'Active'}</span>
                        </>
                      ) : (
                        <span>+{Math.round(p.naturalMaturityAge - years)}y</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
